import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { browserSignals, detectCurrency } from '../lib/currency'
import { FALLBACK_PRICES, isPriceTable, type PriceTable } from '../lib/pricing'

// What the plans cost, in the viewer's currency.
//
// `GET /billing/prices` is the PWA's only view of the Stripe Prices — it needs
// no session (the login page shows a price too), and the function caches it
// for an hour, so this asks once per app load and remembers the answer in
// localStorage for the same hour. Until it arrives, or if it never does,
// FALLBACK_PRICES stands in so the sheet is never blank.
//
// The currency is detected from the device (lib/currency.ts) unless the
// viewer picked one in the subscribe sheet; that pick is kept per browser and
// survives a reload, but only while the Prices still carry it. Whatever this
// returns is what `/checkout` is told to charge in, so the sheet and Checkout
// always agree.

// Versioned: v2 carried the seat amounts and the old SEK prices. A new key
// simply misses, so nobody sees a stale table after the switch.
const PRICES_KEY = 'matlist.prices.v3'
const CURRENCY_KEY = 'matlist.currency'
// Where the pick lived before the app was renamed from Listly. Read only, so a
// returning viewer keeps their currency; the next pick writes the new key.
const LEGACY_CURRENCY_KEY = 'listly.currency'
const TTL_MS = 60 * 60 * 1000

interface Cached { table: PriceTable; at: number }

function readCache(): Cached | null {
  try {
    const raw = localStorage.getItem(PRICES_KEY)
    if (!raw) return null
    const cached = JSON.parse(raw) as Cached
    return isPriceTable(cached?.table) ? cached : null
  } catch {
    return null
  }
}

function writeCache(table: PriceTable) {
  try {
    localStorage.setItem(PRICES_KEY, JSON.stringify({ table, at: Date.now() } satisfies Cached))
  } catch { /* private mode — fine, we just fetch again next time */ }
}

function readOverride(): string | null {
  try {
    return localStorage.getItem(CURRENCY_KEY) ?? localStorage.getItem(LEGACY_CURRENCY_KEY)
  } catch {
    return null
  }
}

export interface Prices {
  table: PriceTable
  /** Lowercase ISO code — always a key of `table.currencies`. */
  currency: string
  /** The currencies the Prices carry, base first. */
  available: string[]
  /** True while the fallback table is what's shown. */
  usingFallback: boolean
  setCurrency: (currency: string) => void
}

export function usePrices(): Prices {
  const [state, setState] = useState<{ table: PriceTable; usingFallback: boolean }>(() => {
    const cached = readCache()
    return cached && Date.now() - cached.at < TTL_MS
      ? { table: cached.table, usingFallback: false }
      : { table: FALLBACK_PRICES, usingFallback: true }
  })
  const [override, setOverride] = useState<string | null>(readOverride)

  useEffect(() => {
    if (!state.usingFallback) return
    let cancelled = false
    supabase.functions.invoke<PriceTable>('billing/prices', { method: 'GET' })
      .then(({ data, error }) => {
        if (cancelled || error || !isPriceTable(data)) return
        writeCache(data)
        setState({ table: data, usingFallback: false })
      })
      .catch(() => { /* offline — the fallback stays */ })
    return () => { cancelled = true }
  }, [state.usingFallback])

  const { table } = state
  const available = [table.base, ...Object.keys(table.currencies).filter(c => c !== table.base)]
  const currency = override && table.currencies[override]
    ? override
    : detectCurrency(available, browserSignals(), table.base)

  function setCurrency(next: string) {
    if (!table.currencies[next]) return
    setOverride(next)
    try { localStorage.setItem(CURRENCY_KEY, next) } catch { /* fine */ }
  }

  return { table, currency, available, usingFallback: state.usingFallback, setCurrency }
}
